"use client";

import { maximumSupplementaryLayouts } from "@tamariba/drills";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { PracticeToolIcon } from "../practice-tool-icon";
import { SkillError, SkillPicker, useSkillCategories } from "../skills/picker";
import { FieldLabel } from "../ui/field-label";
import { DrillBreadcrumbs } from "./breadcrumbs";
import { DrillIcon } from "./icon";
import { LayoutCarousel } from "./layout-carousel";
import { LayoutDialog } from "./layout-dialog";
import {
  currentLayoutDetails,
  parseLayoutDraft,
  restoreLayoutDraft,
  validDraftId,
} from "./layout-draft";
import { DrillError, redirectForAuth, useLeaveWarning } from "./shared";
import {
  type Drill,
  type DrillCollection,
  type DrillWrite,
  type Layout,
  request,
} from "./types";

export function DrillEditor({
  drill,
  collection,
  initialLayoutId,
  draftId,
  onCancel,
  onSaved,
  onSavedAndContinue,
  focusTitle = false,
}: {
  drill?: Drill;
  collection: Pick<DrillCollection, "id" | "title">;
  initialLayoutId?: string;
  draftId?: string;
  onCancel: () => void;
  onSaved: (drill: Drill) => void;
  onSavedAndContinue?: (drill: Drill) => void;
  focusTitle?: boolean;
}) {
  const authoring = useAuthoringScope();
  const router = useRouter();
  const {
    categories: skillCategories,
    error: skillError,
    reload: reloadSkills,
  } = useSkillCategories(authoring.admin);
  const [layouts, setLayouts] = useState<Layout[]>();
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [picking, setPicking] = useState(false);
  const [replaceIndex, setReplaceIndex] = useState<number | null>(null);
  const [notice, setNotice] = useState("");
  const restored = useRef(false);
  const layoutSection = useRef<HTMLDivElement>(null);
  const pickerTrigger = useRef<HTMLElement | null>(null);
  const creationId = useRef("");
  const titleInput = useRef<HTMLInputElement>(null);
  const canContinue = !drill && !!onSavedAndContinue;
  const [form, setForm] = useState<DrillWrite>({
    collectionId: collection.id,
    title: drill?.title ?? "",
    goal: drill?.goal ?? "",
    layoutId: drill?.layout.id ?? "",
    supplementaryLayoutIds:
      drill?.supplementaryLayouts.map((layout) => layout.id) ?? [],
    layoutDetails: drill
      ? [drill.layout, ...drill.supplementaryLayouts].map((layout) => ({
          layoutId: layout.id,
          description: layout.description,
          videoUrl: layout.videoUrl,
        }))
      : [],
    memo: drill?.memo ?? "",
    videoUrl: drill?.videoUrl ?? "",
    references: drill?.references ?? [],
    nextPracticeOn: drill?.nextPracticeOn ?? null,
    revision: drill?.revision ?? 0,
    skillIds: drill?.skills.map((skill) => skill.id) ?? [],
  });
  useEffect(() => {
    if (focusTitle) titleInput.current?.focus({ preventScroll: true });
  }, [focusTitle]);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (!validDraftId(draftId)) return;
    try {
      const key = authoring.draftPrefix + draftId;
      const draft = parseLayoutDraft(sessionStorage.getItem(key), {
        collectionId: collection.id,
        drillId: drill?.id,
      });
      if (!draft) {
        setError(
          "ドリルの入力を復元できませんでした。配置図と入力内容を確認してください。",
        );
        return;
      }
      const { form: next, activeIndex: position } = restoreLayoutDraft(
        draft,
        initialLayoutId,
      );
      creationId.current = draft.creationId;
      setForm(next);
      setDirty(draft.dirty || !!initialLayoutId);
      setActiveIndex(position);
      setNotice(
        initialLayoutId
          ? draft.replaceIndex !== null
            ? "配置図を更新しました。入力中の内容も復元しています。"
            : "作成した配置図を登録しました。ドリルを保存すると確定します。"
          : "作図前の入力内容を復元しました。",
      );
      layoutSection.current?.scrollIntoView({ block: "start" });
      layoutSection.current?.focus({ preventScroll: true });
    } catch {
      setError(
        "ドリルの入力を復元できませんでした。配置図と入力内容を確認してください。",
      );
    }
  }, [draftId, collection.id, drill?.id, initialLayoutId, authoring]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt retries a failed layout request.
  useEffect(() => {
    let active = true;
    setLoadError("");
    request<Layout[]>(authoring.api("/api/layouts"))
      .then((data) => {
        if (!active) return;
        setLayouts(data);
        const initialLayout = initialLayoutId;
        if (
          !draftId &&
          !drill &&
          initialLayout &&
          data.some((layout) => layout.id === initialLayout)
        )
          setForm((form) => ({ ...form, layoutId: initialLayout }));
      })
      .catch((error: Error) => {
        if (active && !redirectForAuth(error, authoring.href("/login")))
          setLoadError(error.message);
      });
    return () => {
      active = false;
    };
  }, [attempt, drill, initialLayoutId, draftId, authoring]);
  useLeaveWarning(dirty && !leaving);
  function change<K extends keyof DrillWrite>(key: K, value: DrillWrite[K]) {
    setDirty(true);
    setForm((form) => ({ ...form, [key]: value }));
  }
  function changeLayoutDetail(
    layoutId: string,
    key: "description" | "videoUrl",
    value: string,
  ) {
    setDirty(true);
    setForm((form) => {
      const details = form.layoutDetails ?? [];
      const current = details.find(
        (detail) => detail.layoutId === layoutId,
      ) ?? { layoutId, description: "", videoUrl: "" };
      return {
        ...form,
        layoutDetails: [
          ...details.filter((detail) => detail.layoutId !== layoutId),
          { ...current, [key]: value },
        ],
      };
    });
  }
  function cancel() {
    if (busy || leaving) return;
    if (!dirty || window.confirm("保存していない変更を破棄しますか？")) {
      clearDrafts();
      onCancel();
    }
  }
  function clearDrafts() {
    try {
      for (const key of Object.keys(sessionStorage)) {
        if (!key.startsWith(authoring.draftPrefix)) continue;
        const draft = parseLayoutDraft(sessionStorage.getItem(key), {
          collectionId: collection.id,
          drillId: drill?.id,
        });
        if (draft) sessionStorage.removeItem(key);
      }
    } catch {
      /* The form can still be saved when storage is unavailable. */
    }
  }
  function openLayoutEditor(editIndex?: number) {
    if (busy || leaving) return;
    try {
      if (!creationId.current)
        creationId.current = drill?.id ?? crypto.randomUUID();
      const token = crypto.randomUUID();
      sessionStorage.setItem(
        authoring.draftPrefix + token,
        JSON.stringify({
          version: 1,
          createdAt: Date.now(),
          drillId: drill?.id ?? null,
          form: { ...form, layoutDetails: currentLayoutDetails(form) },
          creationId: creationId.current,
          dirty,
          replaceIndex: editIndex ?? null,
          activeIndex,
        }),
      );
      const returnTo = drill
        ? authoring.href(`/drills/${drill.id}?edit=1`)
        : authoring.href(
            `/drills/new?collection=${encodeURIComponent(collection.id)}`,
          );
      const params = new URLSearchParams({ returnTo, draft: token });
      const editingId =
        editIndex === undefined ? undefined : layoutIds[editIndex];
      if (!editingId && form.layoutId) params.set("source", form.layoutId);
      // Browser Back must restore the same draft as the explicit return link.
      window.history.replaceState(
        window.history.state,
        "",
        `${returnTo}&draft=${token}`,
      );
      setLeaving(true);
      router.push(authoring.href(`/layouts/${editingId ?? "new"}?${params}`));
    } catch {
      setError(
        "作図中の入力を一時保持できませんでした。ブラウザの保存領域を利用できる状態にして、再度お試しください。",
      );
    }
  }
  function selectLayout(layout: Layout) {
    const ids = [form.layoutId, ...(form.supplementaryLayoutIds ?? [])].filter(
      Boolean,
    );
    const position = replaceIndex ?? ids.length;
    ids.splice(position, replaceIndex === null ? 0 : 1, layout.id);
    setDirty(true);
    setForm((form) => ({
      ...form,
      layoutId: ids[0] ?? "",
      supplementaryLayoutIds: ids.slice(1),
    }));
    setActiveIndex(position);
    setPicking(false);
    setNotice(
      `「${layout.title}」を${position === 0 ? "メイン配置図" : "サブ配置図"}に登録しました。`,
    );
  }
  async function save(continueAdding = false) {
    if (busy || leaving) return;
    if (!form.layoutId) {
      setError("メイン配置図を登録してください。");
      layoutSection.current?.focus();
      return;
    }
    if (missingLayoutIds.length > 0) {
      setError(
        "削除された配置図があります。配置図を選び直すか、サブ配置図をドリルから外してください。",
      );
      layoutSection.current?.focus();
      return;
    }
    setBusy(true);
    setError("");
    if (!creationId.current)
      creationId.current = drill?.id ?? crypto.randomUUID();
    try {
      const saved = await request<Drill>(
        authoring.api(`/api/drills/${creationId.current}`),
        {
          ...form,
          layoutDetails: currentLayoutDetails(form),
        },
      );
      setDirty(false);
      clearDrafts();
      if (continueAdding && onSavedAndContinue) onSavedAndContinue(saved);
      else onSaved(saved);
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const layoutIds = [
    form.layoutId,
    ...(form.supplementaryLayoutIds ?? []),
  ].filter(Boolean);
  const savedLayouts = drill
    ? [drill.layout, ...drill.supplementaryLayouts]
    : [];
  const missingLayoutIds = layouts
    ? layoutIds.filter((id) => !layouts.some((layout) => layout.id === id))
    : [];
  const selectedLayouts = layoutIds.map((id) => ({
    ...(layouts?.find((layout) => layout.id === id) ??
      savedLayouts.find((layout) => layout.id === id) ?? {
        id,
        title: layouts ? "配置図が見つかりません" : "配置図を読み込み中…",
        revision: 1,
      }),
    description:
      form.layoutDetails?.find((detail) => detail.layoutId === id)
        ?.description ?? "",
    videoUrl:
      form.layoutDetails?.find((detail) => detail.layoutId === id)?.videoUrl ??
      "",
  }));
  return (
    <div className="drill-editor-page drill-single-editor-page">
      <DrillBreadcrumbs
        collection={collection}
        current={drill ? "ドリルを編集" : "ドリルを追加"}
      />
      <button
        type="button"
        className="drill-back text-link"
        onClick={cancel}
        disabled={busy || leaving}
      >
        <DrillIcon name="back" size={16} />{" "}
        {drill ? "ドリルに戻る" : "ドリル集に戻る"}
      </button>
      <div className="drill-heading drill-editor-heading">
        <div>
          <h1>{drill ? "ドリルを編集" : "ドリルを追加"}</h1>
          <p className="drill-description">
            成功条件を決めて、練習する配置と説明を登録します。
          </p>
          <div className="drill-add-destination">
            <PracticeToolIcon name="drills" size={19} />
            <span>
              {drill ? "所属するドリル集" : "追加先のドリル集"}
              <strong>{collection.title}</strong>
            </span>
          </div>
        </div>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save(canContinue);
        }}
        className="drill-edit-form drill-designed-form"
      >
        <fieldset disabled={busy || leaving} className="drill-editor-fields">
          <section
            className="drill-panel"
            aria-labelledby="drill-basics-heading"
          >
            <div className="drill-editor-section-heading">
              <span aria-hidden="true">01</span>
              <div>
                <h2 id="drill-basics-heading">基本情報と成功条件</h2>
                <p>どの配置図にも共通する、このドリルの目標です。</p>
              </div>
            </div>
            <label className="drill-field">
              <FieldLabel required>ドリル名</FieldLabel>
              <input
                ref={titleInput}
                required
                maxLength={120}
                value={form.title}
                onChange={(event) => change("title", event.target.value)}
                placeholder="例：ロングショットをまっすぐに"
              />
            </label>
            <label className="drill-field">
              <FieldLabel required={false}>目標・成功条件</FieldLabel>
              <textarea
                rows={3}
                maxLength={1000}
                value={form.goal}
                onChange={(event) => change("goal", event.target.value)}
                placeholder="例：的球をコーナーに入れ、手玉を指定エリアに止める。"
              />
              <small>何ができたら「成功」かを決めておきましょう。</small>
            </label>
          </section>
          <section
            className="drill-panel"
            aria-labelledby="drill-skills-heading"
          >
            <div className="drill-editor-section-heading">
              <span aria-hidden="true">02</span>
              <div>
                <h2 id="drill-skills-heading">鍛えられるスキル</h2>
                <p>
                  このドリルで練習するスキルや条件を選びましょう。弱点に関連するドリルの検索に使います。
                </p>
              </div>
            </div>
            {skillCategories ? (
              <SkillPicker
                categories={skillCategories}
                selectedIds={
                  form.skillIds ?? drill?.skills.map((skill) => skill.id) ?? []
                }
                selectedSkills={drill?.skills}
                onChange={(ids) => change("skillIds", ids)}
                disabled={busy || leaving}
              />
            ) : (
              !skillError && <p role="status">スキルを読み込み中…</p>
            )}
            {skillError && (
              <>
                <SkillError message={skillError} />
                <button
                  type="button"
                  className="secondary-button"
                  onClick={reloadSkills}
                >
                  スキルを再読み込み
                </button>
              </>
            )}
          </section>
          <section
            className="drill-panel"
            aria-labelledby="drill-layouts-heading"
          >
            <div className="drill-editor-section-heading">
              <span aria-hidden="true">03</span>
              <div>
                <h2 id="drill-layouts-heading">配置図と説明</h2>
                <p>1枚目がメイン。狙い方などの例はサブ配置図に追加します。</p>
              </div>
            </div>
            <div
              ref={layoutSection}
              tabIndex={-1}
              className="drill-layout-section"
            >
              <LayoutCarousel
                layouts={selectedLayouts}
                activeIndex={activeIndex}
                onActiveChange={setActiveIndex}
                showDetails={false}
                emptySlide={
                  layoutIds.length < maximumSupplementaryLayouts + 1 ? (
                    <div className="drill-layout-empty-slot">
                      <PracticeToolIcon name="layouts" size={32} />
                      <strong>
                        {layoutIds.length === 0
                          ? "メイン配置図を登録"
                          : "サブ配置図を追加"}
                      </strong>
                      <div className="drill-layout-slot-buttons">
                        <button
                          type="button"
                          className="primary-button"
                          onClick={() => openLayoutEditor()}
                        >
                          <DrillIcon name="plus" size={18} />
                          配置図を作成し使用
                        </button>
                        <button
                          type="button"
                          className="secondary-button"
                          aria-haspopup="dialog"
                          onClick={(event) => {
                            pickerTrigger.current = event.currentTarget;
                            setReplaceIndex(null);
                            setPicking(true);
                          }}
                        >
                          <DrillIcon name="collection" size={18} />
                          既存の配置図を使用
                        </button>
                      </div>
                      <p>
                        {form.layoutId
                          ? "メイン配置図をコピーして作成します。"
                          : "保存すると、このドリルに戻ります。"}
                      </p>
                    </div>
                  ) : undefined
                }
                actions={(index) => (
                  <div className="drill-layout-slide-actions">
                    <button
                      type="button"
                      className="text-link"
                      onClick={() => openLayoutEditor(index)}
                    >
                      <DrillIcon name="edit" size={15} />
                      配置図を編集
                    </button>
                    <button
                      type="button"
                      className="text-link"
                      onClick={(event) => {
                        pickerTrigger.current = event.currentTarget;
                        setReplaceIndex(index);
                        setPicking(true);
                      }}
                    >
                      この配置図を選び直す
                    </button>
                    {index > 0 && (
                      <button
                        type="button"
                        className="text-link drill-layout-remove"
                        onClick={() => {
                          change(
                            "supplementaryLayoutIds",
                            (form.supplementaryLayoutIds ?? []).filter(
                              (_, position) => position !== index - 1,
                            ),
                          );
                          setActiveIndex(index - 1);
                          setNotice("サブ配置図をドリルから外しました。");
                          layoutSection.current?.focus({ preventScroll: true });
                        }}
                      >
                        サブ配置図を外す
                      </button>
                    )}
                  </div>
                )}
              />
              {missingLayoutIds.includes(layoutIds[activeIndex] ?? "") && (
                <p role="alert">
                  この配置図は削除されたか、表示できなくなっています。配置図を選び直してください。
                </p>
              )}
              {selectedLayouts[activeIndex] && (
                <div
                  className="drill-layout-detail-editor"
                  key={layoutIds[activeIndex]}
                >
                  <p className="drill-layout-editing-label">
                    {activeIndex === 0
                      ? "メイン配置図"
                      : `サブ配置図 ${activeIndex}`}
                    の説明・動画
                  </p>
                  <p className="drill-editor-layout-context">
                    表示中の配置図の説明です。配置図を切り替えると、入力欄も切り替わります。
                  </p>
                  <label className="drill-field">
                    <FieldLabel required={false}>この配置図の説明</FieldLabel>
                    <textarea
                      rows={3}
                      maxLength={2000}
                      value={selectedLayouts[activeIndex]?.description ?? ""}
                      onChange={(event) =>
                        changeLayoutDetail(
                          layoutIds[activeIndex] ?? "",
                          "description",
                          event.target.value,
                        )
                      }
                      placeholder="この配置で意識することや、狙い方のポイント。"
                    />
                  </label>
                  <label className="drill-field">
                    <FieldLabel required={false}>
                      この配置図の動画URL
                    </FieldLabel>
                    <input
                      type="url"
                      pattern="https://.*"
                      maxLength={2000}
                      value={selectedLayouts[activeIndex]?.videoUrl ?? ""}
                      onChange={(event) =>
                        changeLayoutDetail(
                          layoutIds[activeIndex] ?? "",
                          "videoUrl",
                          event.target.value,
                        )
                      }
                      placeholder="https://…"
                    />
                    <small>
                      HTTPSで始まる動画ページのURLを入力してください。
                    </small>
                  </label>
                </div>
              )}
              {layoutIds.length >= maximumSupplementaryLayouts + 1 && (
                <p className="drill-layout-hint">
                  サブ配置図は{maximumSupplementaryLayouts}枚まで登録できます。
                </p>
              )}
              {notice && (
                <p className="drill-layout-notice" role="status">
                  {notice}
                </p>
              )}
            </div>
          </section>
        </fieldset>
        {error && (
          <>
            <DrillError message={error} />
            <a
              className="text-link"
              href={authoring.href(`/drills/${creationId.current}`)}
              target="_blank"
              rel="noreferrer"
            >
              保存先を別のタブで確認 <DrillIcon name="external" size={14} />
            </a>
          </>
        )}
        <div
          className={`drill-form-actions drill-editor-savebar${canContinue ? " drill-editor-add-actions" : ""}`}
        >
          <button
            type="button"
            className={canContinue ? "text-link" : "secondary-button"}
            disabled={busy || leaving}
            onClick={cancel}
          >
            キャンセル
          </button>
          {canContinue && (
            <button
              type="button"
              className="secondary-button"
              disabled={busy || leaving}
              onClick={(event) => {
                if (event.currentTarget.form?.reportValidity()) void save();
              }}
            >
              追加して完了
              <DrillIcon name="check" size={18} />
            </button>
          )}
          <button
            type="submit"
            className="primary-button"
            disabled={busy || leaving}
          >
            {busy
              ? "保存中…"
              : drill
                ? "変更を保存"
                : canContinue
                  ? "追加して次へ"
                  : "ドリルを追加"}
            <DrillIcon name={canContinue ? "arrow" : "check"} size={18} />
          </button>
        </div>
      </form>
      {picking && (
        <LayoutDialog
          layouts={layouts}
          error={loadError}
          excludedIds={layoutIds.filter((_, index) => index !== replaceIndex)}
          onRetry={() => setAttempt((attempt) => attempt + 1)}
          onClose={() => setPicking(false)}
          onSelect={selectLayout}
          returnFocus={pickerTrigger.current}
        />
      )}
    </div>
  );
}
